import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Eye, EyeOff, Globe, Lock, LogIn, Shield, Truck, User } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useSettings } from '../contexts/SettingsContext'
import type { MfaChallenge, MfaMethod } from '../services/authService'
import AnimatedTrackingBackdrop from './AnimatedTrackingBackdrop'
import SiniBrand from './SiniBrand'
import { AuthError, MfaEnrollmentForm, MfaVerificationForm, RecoveryCodesPanel } from './AuthSecuritySteps'

type Stage = 'credentials' | 'enrollment' | 'verification' | 'recovery'

const LoginPage: React.FC = () => {
  const {
    login, verifyMfa, sendMfaEmail, sendMfaEnrollmentEmail, confirmMfaEnrollment,
    isAuthenticated, isLoading
  } = useAuth()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { get } = useSettings()
  const appName = String(get('branding.app_name', t('login_title')))
  const [credentials, setCredentials] = useState({ username: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [stage, setStage] = useState<Stage>('credentials')
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])
  useEffect(() => {
    if (isAuthenticated && !isLoading && stage === 'credentials') navigate('/dashboard')
  }, [isAuthenticated, isLoading, navigate, stage])

  const handleResult = (result: Awaited<ReturnType<typeof login>>) => {
    if (result.status === 'error') return setError(result.error)
    if (result.status === 'mfa_required') {
      setChallenge(result.challenge)
      setStage(result.challenge.enrollmentRequired ? 'enrollment' : 'verification')
      return
    }
    if (result.recoveryCodes?.length) {
      setRecoveryCodes(result.recoveryCodes)
      setStage('recovery')
    } else navigate('/dashboard')
  }

  // Abandon the second factor: the challenge token is simply dropped (it
  // expires server-side) and the password is cleared so it must be retyped.
  const cancelChallenge = () => {
    setChallenge(null)
    setError(null)
    setCredentials(current => ({ ...current, password: '' }))
    setStage('credentials')
  }

  const submitCredentials = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      handleResult(await login(credentials))
    } finally {
      setBusy(false)
    }
  }

  const verify = async (method: MfaMethod, code: string) => {
    if (!challenge) return
    setBusy(true)
    setError(null)
    try {
      handleResult(await verifyMfa(challenge.challengeToken, method, code))
    } finally {
      setBusy(false)
    }
  }

  const confirmEnrollment = async (method: Exclude<MfaMethod, 'recovery'>, code: string) => {
    if (!challenge) return
    setBusy(true)
    setError(null)
    try {
      handleResult(await confirmMfaEnrollment(challenge.challengeToken, method, code))
    } finally {
      setBusy(false)
    }
  }

  const sendEmail = async () => {
    if (!challenge) return false
    const result = await sendMfaEmail(challenge.challengeToken)
    if (!result.success) setError(result.error || t('login_error'))
    return result.success
  }

  const sendEnrollmentEmail = async () => {
    if (!challenge) return false
    const result = await sendMfaEnrollmentEmail(challenge.challengeToken)
    if (!result.success) setError(result.error || t('login_error'))
    return result.success
  }

  if (isLoading && stage === 'credentials') {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
    </div>
  }

  return (
    <div className="min-h-screen flex bg-slate-50 relative overflow-hidden">
      <AnimatedTrackingBackdrop reduceMotion={get('appearance.reduced_motion', false)} />
      <div className="absolute top-6 right-6 z-30">
        <div className="relative">
          <button onClick={() => setLangOpen(value => !value)}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white/80 px-3 py-2 text-sm text-slate-700 shadow-sm backdrop-blur">
            <Globe size={16} /><span className="font-medium uppercase">{i18n.language?.slice(0, 2)}</span><ChevronDown size={14} />
          </button>
          {langOpen && <div className="absolute right-0 mt-2 w-40 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
            {['en', 'fr'].map(lang => <button key={lang} onClick={() => { i18n.changeLanguage(lang); setLangOpen(false) }}
              className="block w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50">
              {lang.toUpperCase()} · {t(lang === 'en' ? 'english' : 'french')}
            </button>)}
          </div>}
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 relative">
        <div className={`max-w-lg transition-all duration-700 ${mounted ? 'opacity-100' : 'opacity-0 -translate-x-8'}`}>
          <div className="inline-flex rounded-2xl border border-slate-200 bg-white/80 px-5 py-4 shadow-xl backdrop-blur"><SiniBrand /></div>
          <h1 className="mt-6 text-3xl font-bold text-slate-900">{appName}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('login_subtitle')}</p>
          <div className="mt-10 space-y-5">
            <div className="flex gap-4"><Shield className="text-blue-600" /><div><b className="text-sm">{t('secure_access')}</b><p className="text-sm text-slate-500">{t('secure_access_desc')}</p></div></div>
            <div className="flex gap-4"><Truck className="text-blue-600" /><div><b className="text-sm">{t('fleet_operations')}</b><p className="text-sm text-slate-500">{t('fleet_operations_desc')}</p></div></div>
          </div>
          <div className="mt-12 text-xs text-slate-400">fleet.sinigroupe.com</div>
        </div>
      </div>

      <div className="flex w-full items-center justify-center p-6 sm:p-12 lg:w-1/2">
        <div className={`w-full max-w-md transition-all duration-700 ${mounted ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
          <div className="mb-7 flex flex-col items-center gap-3 lg:hidden">
            <div className="rounded-xl border border-slate-200 bg-white/80 px-4 py-3 shadow-sm"><SiniBrand /></div>
            <h1 className="text-xl font-bold text-slate-900">{appName}</h1>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white/90 p-8 shadow-xl backdrop-blur-xl">
            {stage === 'credentials' && (
              <form onSubmit={submitCredentials} className="space-y-5">
                <div><h2 className="text-2xl font-bold text-slate-900">{t('welcome_back')}</h2><p className="mt-1 text-sm text-slate-500">{t('login_description')}</p></div>
                <AuthError message={error} />
                <label className="block text-sm font-medium text-slate-700">{t('username')}
                  <div className="relative mt-2"><User className="absolute left-3.5 top-3 text-slate-400" size={18} />
                    <input value={credentials.username} onChange={event => setCredentials(current => ({ ...current, username: event.target.value }))}
                      autoComplete="username" required className="w-full rounded-xl border border-slate-300 py-3 pl-11 pr-4 text-sm outline-none focus:border-blue-500" />
                  </div>
                </label>
                <label className="block text-sm font-medium text-slate-700">{t('password')}
                  <div className="relative mt-2"><Lock className="absolute left-3.5 top-3 text-slate-400" size={18} />
                    <input type={showPassword ? 'text' : 'password'} value={credentials.password}
                      onChange={event => setCredentials(current => ({ ...current, password: event.target.value }))}
                      autoComplete="current-password" required className="w-full rounded-xl border border-slate-300 py-3 pl-11 pr-11 text-sm outline-none focus:border-blue-500" />
                    <button type="button" onClick={() => setShowPassword(value => !value)} className="absolute right-3.5 top-3 text-slate-400">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </label>
                <div className="flex justify-end">
                  <Link to="/forgot-password" className="text-xs font-medium text-blue-600 hover:text-blue-700">
                    {t('forgot_password')}
                  </Link>
                </div>
                <button disabled={busy || !credentials.username || !credentials.password}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">
                  <LogIn size={18} />{busy ? t('signing_in') : t('sign_in')}
                </button>
              </form>
            )}
            {stage === 'verification' && challenge && <MfaVerificationForm challenge={challenge} busy={busy} error={error}
              onVerify={verify} onSendEmail={sendEmail} onCancel={cancelChallenge} />}
            {stage === 'enrollment' && challenge && <MfaEnrollmentForm challenge={challenge} busy={busy} error={error}
              onConfirm={confirmEnrollment} onSendEmail={sendEnrollmentEmail} onCancel={cancelChallenge} />}
            {stage === 'recovery' && <RecoveryCodesPanel codes={recoveryCodes} onContinue={() => navigate('/dashboard')} />}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
